import * as React from "react";
import { useParams, Link } from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  Button,
  IconButton,
  Container,
  CssBaseline,
  Toolbar,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import EventIcon from "@mui/icons-material/Event";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PeopleIcon from "@mui/icons-material/People";
import CodeIcon from "@mui/icons-material/Code";
import Footer from "../components/Footer";
import Navigation from "../components/Navigation";
import AdminPageHeader from "../components/AdminPageHeader";
import { useApi } from "../hooks/useApi";
import { useQuery } from "@tanstack/react-query";

function formatDate(dateString) {
  if (!dateString) return "N/A";
  try {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return dateString;
  }
}

function formatTime(dateString) {
  if (!dateString) return "N/A";
  try {
    return new Date(dateString).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
    });
  } catch {
    return dateString;
  }
}

function isUpcoming(startsAt) {
  if (!startsAt) return false;
  return new Date(startsAt) >= new Date();
}

function formatDateTime(dateString) {
  if (!dateString) return "N/A";
  try {
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return dateString;
  }
}

function formatStatus(status) {
  if (!status) return "N/A";
  const s = String(status).toLowerCase();
  if (s === "going" || s === "yes") return "Going";
  if (s === "maybe") return "Maybe";
  if (s === "not_going" || s === "no") return "Not going";
  return status;
}

export default function CircleEventDetail() {
  const { id } = useParams();
  const api = useApi();
  const [event, setEvent] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [attendeePage, setAttendeePage] = React.useState(1);
  const [expandedAttendeeId, setExpandedAttendeeId] = React.useState(null);

  React.useEffect(() => {
    const fetchEvent = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await api.get(`/admin/circle/events/${id}`);
        setEvent(data);
      } catch (err) {
        console.error("Error fetching event:", err);
        setError(err.message || "Failed to fetch event");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchEvent();
      setAttendeePage(1);
      setExpandedAttendeeId(null);
    }
  }, [id, api]);

  const { data: attendeesData, isLoading: attendeesLoading } = useQuery({
    queryKey: ["circle-event-attendees", id, attendeePage],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set("page", String(attendeePage));
      params.set("per_page", "50");
      return api.get(`/admin/circle/events/${id}/attendees?${params.toString()}`);
    },
    enabled: !!id && !!event,
  });

  const attendees = attendeesData?.attendees ?? [];

  React.useEffect(() => {
    setExpandedAttendeeId(null);
  }, [attendeePage]);

  const attendeesPagination = {
    page: attendeesData?.page ?? attendeePage,
    per_page: attendeesData?.per_page ?? 50,
    count: attendeesData?.count ?? 0,
    has_next_page: attendeesData?.has_next_page ?? false,
    page_count: attendeesData?.page_count,
  };

  const startAt = event?.starts_at || event?.start_at;
  const eventName = event?.name || "Untitled";

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <Navigation />
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) => theme.palette.background.default,
            flexGrow: 1,
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
          }}
        >
          <Toolbar />
          <Container maxWidth="lg" sx={{ mt: 2, mb: 4, flexGrow: 1 }}>
            <Box sx={{ mb: 2 }}>
              <Breadcrumbs aria-label="breadcrumb">
                <Typography
                  color="primary"
                  component={Link}
                  to="/dashboard"
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Home
                </Typography>
                <Typography
                  color="primary"
                  component={Link}
                  to="/dashboard/circle"
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Circle
                </Typography>
                <Typography
                  color="primary"
                  component={Link}
                  to="/dashboard/circle/events"
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Events
                </Typography>
                <Typography color="text.primary" fontWeight={500}>
                  {eventName}
                </Typography>
              </Breadcrumbs>
            </Box>

            <AdminPageHeader
              title="Event Details"
              description="View Circle community event information"
              icon={<EventIcon sx={{ color: "primary.main" }} />}
            />

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
              </Box>
            ) : event ? (
              <Card variant="outlined">
                <CardContent sx={{ pt: 2, pb: 2 }}>
                  <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ mb: 1.5 }}>
                    Event Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                        Name
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25 }}>
                        {event.name || "Untitled"}
                      </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                        Date
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25 }}>
                        {formatDate(startAt)}
                      </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                        Time
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25 }}>
                        {formatTime(startAt)}
                      </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                        Status
                      </Typography>
                      <Box sx={{ mt: 0.25 }}>
                        <Chip
                          label={isUpcoming(startAt) ? "Upcoming" : "Past"}
                          size="small"
                          color={isUpcoming(startAt) ? "success" : "default"}
                          variant="outlined"
                        />
                      </Box>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                        Event ID
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.25 }} fontFamily="monospace">
                        {event.id || "N/A"}
                      </Typography>
                    </Grid>

                    {event.url && (
                      <Grid size={{ xs: 12 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                          URL
                        </Typography>
                        <Typography
                          component="a"
                          href={event.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="body2"
                          sx={{ mt: 0.25, display: "block", color: "primary.main", textDecoration: "underline" }}
                        >
                          {event.url}
                        </Typography>
                      </Grid>
                    )}

                    {event.body && (
                      <Grid size={{ xs: 12 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block">
                          Description
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            mt: 0.25,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                          }}
                        >
                          {event.body}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            ) : null}

            {event && (
              <Card variant="outlined" sx={{ mt: 3 }}>
                <CardContent sx={{ pt: 2, pb: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <PeopleIcon color="primary" />
                    <Typography variant="h6" fontWeight="bold">
                      Attendees
                    </Typography>
                  </Box>
                  {attendeesLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                      <CircularProgress size={32} />
                    </Box>
                  ) : (
                    <Paper variant="outlined" sx={{ borderRadius: 2 }}>
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Name</TableCell>
                              <TableCell>Email</TableCell>
                              <TableCell>RSVP / Type</TableCell>
                              <TableCell>Registered</TableCell>
                              <TableCell align="right" width={100}>
                                JSON
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {attendees.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                  <Typography color="text.secondary">
                                    No attendees yet
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ) : (
                              attendees.map((attendee) => {
                                const status =
                                  attendee.rsvp_status ?? attendee.status;
                                const registeredAt =
                                  attendee.rsvp_date ?? attendee.created_at;
                                return (
                                <React.Fragment key={attendee.id}>
                                  <TableRow>
                                    <TableCell>
                                      {(attendee.community_member_id ??
                                        attendee.member_id ??
                                        attendee.user_id) ? (
                                        <Typography
                                          component={Link}
                                          to={`/dashboard/circle/users/${
                                            attendee.community_member_id ??
                                            attendee.member_id ??
                                            attendee.user_id
                                          }`}
                                          sx={{
                                            color: "primary.main",
                                            textDecoration: "none",
                                            fontWeight: 500,
                                            "&:hover": { textDecoration: "underline" },
                                          }}
                                        >
                                          {attendee.member_name ?? "—"}
                                        </Typography>
                                      ) : (
                                        attendee.member_name ?? "—"
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {(attendee.community_member_id ??
                                        attendee.member_id ??
                                        attendee.user_id) ? (
                                        <Typography
                                          component={Link}
                                          to={`/dashboard/circle/users/${
                                            attendee.community_member_id ??
                                            attendee.member_id ??
                                            attendee.user_id
                                          }`}
                                          sx={{
                                            color: "primary.main",
                                            textDecoration: "none",
                                            "&:hover": { textDecoration: "underline" },
                                          }}
                                        >
                                          {attendee.member_email ?? "—"}
                                        </Typography>
                                      ) : (
                                        attendee.member_email ?? "—"
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                                        <Chip
                                          label={formatStatus(status)}
                                          size="small"
                                          variant="outlined"
                                          color={
                                            status === "going" || status === "yes"
                                              ? "success"
                                              : status === "maybe"
                                                ? "warning"
                                                : "default"
                                          }
                                        />
                                        {attendee.contact_type && (
                                          <Typography variant="caption" color="text.secondary">
                                            {attendee.contact_type}
                                          </Typography>
                                        )}
                                      </Box>
                                    </TableCell>
                                    <TableCell>
                                      {formatDateTime(registeredAt)}
                                    </TableCell>
                                    <TableCell align="right">
                                      <IconButton
                                        size="small"
                                        color={expandedAttendeeId === attendee.id ? "primary" : "default"}
                                        onClick={() =>
                                          setExpandedAttendeeId((prev) =>
                                            prev === attendee.id ? null : attendee.id
                                          )
                                        }
                                        aria-label={
                                          expandedAttendeeId === attendee.id
                                            ? "Hide JSON"
                                            : "Show JSON"
                                        }
                                      >
                                        <CodeIcon />
                                      </IconButton>
                                    </TableCell>
                                  </TableRow>
                                  {expandedAttendeeId === attendee.id && (
                                    <TableRow>
                                      <TableCell
                                        colSpan={5}
                                        sx={{
                                          py: 0,
                                          borderBottom: 1,
                                          borderColor: "divider",
                                          bgcolor: (theme) =>
                                            theme.palette.mode === "dark"
                                              ? "grey.900"
                                              : "grey.50",
                                        }}
                                      >
                                        <Box
                                          component="pre"
                                          sx={{
                                            p: 2,
                                            m: 0,
                                            fontSize: "0.75rem",
                                            fontFamily: "monospace",
                                            overflow: "auto",
                                            maxHeight: 300,
                                          }}
                                        >
                                          {JSON.stringify(attendee, null, 2)}
                                        </Box>
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </React.Fragment>
                              );
                              })
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      {(attendeesPagination.count > 0 || attendees.length > 0) && (
                        <Box
                          sx={{
                            px: 2,
                            py: 1.5,
                            borderTop: 1,
                            borderColor: "divider",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            flexWrap: "wrap",
                            gap: 1,
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            Page {attendeesPagination.page}
                            {attendeesPagination.page_count != null &&
                              ` of ${attendeesPagination.page_count}`}{" "}
                            • {attendees.length} on this page
                            {attendeesPagination.count > 0 &&
                              ` • ${attendeesPagination.count} total`}
                          </Typography>
                          <Box sx={{ display: "flex", gap: 0.5 }}>
                            <Button
                              size="small"
                              disabled={
                                attendeesPagination.page <= 1 || attendeesLoading
                              }
                              onClick={() =>
                                setAttendeePage((p) => Math.max(1, p - 1))
                              }
                              startIcon={<ChevronLeftIcon />}
                            >
                              Previous
                            </Button>
                            <Button
                              size="small"
                              disabled={
                                !attendeesPagination.has_next_page ||
                                attendeesLoading
                              }
                              onClick={() =>
                                setAttendeePage((p) => p + 1)
                              }
                              endIcon={<ChevronRightIcon />}
                            >
                              Next
                            </Button>
                          </Box>
                        </Box>
                      )}
                    </Paper>
                  )}
                </CardContent>
              </Card>
            )}
            {!loading && !event && (
              <Alert severity="warning">Event not found</Alert>
            )}
          </Container>
          <Footer />
        </Box>
      </Box>
    </>
  );
}
